import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login, googleLogin } from "../../actions/authActions";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  Image,
  Flex,
  Divider,
  AbsoluteCenter,
  Icon,
  Spinner,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import {
  primary,
  white,
  newBorder,
  textSecondary,
  textPrimary,
  dark,
  primaryAlt,
} from "../../utils/colors";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import GoogleButton from "../../components/buttons/GoogleButton";
import { useGoogleLogin } from "@react-oauth/google"; // Import Google login
import PasswordInput from "../../components/input/PasswordInput";
import { FaArrowLeft, FaArrowRightLong } from "react-icons/fa6";
import dentacasa from "../../assets/icons/dentacasa.svg";
import {
  AUTH_LOGIN_RESET,
  AUTH_LOGIN_SUCCESS,
  AUTH_GOOGLE_LOGIN_SUCCESS,
  AUTH_VERIFY_LOGIN_CODE_RESET,
  AUTH_GOOGLE_LOGIN_RESET,
} from "../../constants/authConstants";

import testupscale from "../../assets/images/testupscale.png";
import RequestResetPassword from "./partials/RequestResetPassword";
import useIsMobile from "../../hooks/useIsMobile";
import LanguageButtonMain from "../../components/buttons/LanguageButtonMain";

const Login = () => {
  // GENERAL
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // LANGUAGE
  const storedLanguage = localStorage.getItem("language");
  const initialLanguage = storedLanguage || "en"; // Default to 'en'
  const [currentLanguage, setCurrentLanguage] = useState(initialLanguage);

  // STATE
  const [isResetPasswordOpen, setResetPasswordOpen] = useState(false);

  const [showVerification, setShowVerification] = useState(false);

  // State for managing video load
  const [videoLoaded, setVideoLoaded] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // REDUX
  const dispatch = useDispatch();

  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo, loading: loginLoading, error: loginError } = authLogin;

  const authGoogleLogin = useSelector((state) => state.authGoogleLogin);
  const {
    userInfo: googleUserInfo,
    loading: googleLoginLoading,
    error: googleLoginError,
  } = authGoogleLogin;

  // HANDLERS

  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // console.log("Google Token:", tokenResponse);
        const { access_token } = tokenResponse;

        await dispatch(googleLogin(access_token));
      } catch (error) {
        console.error("Google Login Error:", error.response?.data || error);
      }
    },
    onError: (error) => console.error("Google Login Failed", error),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // setLoading(true);
    try {
      await dispatch(login(email, password, currentLanguage)); // Add necessary fields
    } catch (error) {
      // setLoading(false);
      console.error("Login failed", error);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  useEffect(() => {
    if (loginError) {
      if (
        loginError ===
        "This account uses Google login. Please log in using Google."
      ) {
        toast({
          title: t("toasts.loginGoogleError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else if (loginError === "This email is not linked with Google login.") {
        toast({
          title: t("toasts.loginNoGoogleError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else if (
        loginError ===
        "This account does not have access to any active clinics."
      ) {
        toast({
          title: t("toasts.loginInactiveError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else if (loginError === "Invalid email") {
        toast({
          title: t("toasts.loginEmailError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else if (loginError === "Invalid password") {
        toast({
          title: t("toasts.loginPasswordError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("toasts.loginError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }

      dispatch({ type: AUTH_LOGIN_RESET });

      // console.log("Login error:", loginError);
    }
  }, [loginError]);

  useEffect(() => {
    if (googleLoginError) {
      toast({
        title: t("toasts.googleLoginError"),
        // description: error,
        status: "error",

        duration: 3000,

        isClosable: true,
      });
      dispatch({ type: AUTH_GOOGLE_LOGIN_RESET });
    }
  }, [googleLoginError]);

  useEffect(() => {
    if (videoLoaded) {
      // console.log("Video loaded");
    }
  }, [videoLoaded]);

  useEffect(() => {
    if (userInfo) {
      if (userInfo.type === "Single") {
        navigate(`/${userInfo.tenants[0].slug}/dashboard`);
      } else if (userInfo.type === "Multiple") {
        navigate("/select-tenant");
      }
    }
  }, [userInfo, navigate]);

  // LOGS

  // console.log("userInfo", userInfo);
  // console.log("googleUserInfo", googleUserInfo);

  // console.log("googleLoginError", googleLoginError);
  // console.log("loginError", loginError);

  // RENDER
  return (
    <>
      <Helmet>
        <title>{t("pageTitle.login")}</title>
        <meta name="description" content={t("pageDescription.login")} />
      </Helmet>

      <Flex
        w="100%"
        h={["100%", "100vh"]}
        align="stretch"
        position={["relative", "relative", "relative"]}
        justify="flex-start"
        bg={white}
        as={motion.div}
        exit={{ opacity: 0, x: 0, transition: { duration: 0.15 } }}
        initial={{ opacity: 0, x: 0, y: 0 }}
        animate={{
          opacity: 1,
          x: 0,
          y: 0,
          transition: { duration: 0.15 },
        }}
      >
        <Box
          position={["absolute", "fixed"]}
          top={["0rem", "2.5rem"]}
          left={["0rem", "0rem"]}
          // bg="green"
          p={["1.5rem 1.5rem", "0 3rem"]}
          w={["100%", "100%"]}
          zIndex={1000}
        >
          <Flex w="100%" align="center" justify="space-between">
            <Link to="/">
              <Box h={["20px", "22px"]} w="initial">
                <Image src={dentacasa} h="100%" w="initial" />
              </Box>
            </Link>
            <Flex align="center" justify="flex-end" gap="0rem">
              <LanguageButtonMain />
            </Flex>
          </Flex>
        </Box>

        <Box
          w={["100%", "29%"]}
          position={["fixed", "relative", "relative"]}
          h="100%"
          flexShrink={0}
          maxH={["100%", "100vh"]}
          display={["none", "flex"]}
        >
          <Flex w="100%" h="100%" position="relative">
            <Image src={testupscale} w="100%" h="100%" objectFit="cover" />
            <Flex
              position="absolute"
              zIndex="0"
              top="0"
              left="0"
              w="100%"
              h="100%"
              as={motion.div}
              initial={{ opacity: 0, y: 0 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: { delay: 0, duration: 1 },
              }}
            >
              <Video
                playsInline
                autoPlay
                loop
                muted
                onLoadedData={() => setVideoLoaded(true)}
                onError={() => setVideoLoaded(false)}
                style={{ display: videoLoaded ? "block" : "none" }}
                src="https://bucket.mailersendapp.com/0p7kx4xmrvl9yjre/z3m5jgr1omx4dpyo/files/9ca7a800-8d36-414c-a9f1-d5ac05d4a695/videonew2.mp4"
              ></Video>
            </Flex>
          </Flex>
        </Box>

        <Flex
          w="100%"
          h={["100%", "100%", "100%"]}
          p={["12vh 0rem 3rem", "0px 0px 0px 100px"]}
          align="flex-start"
          justify={["flex-start", "center"]}
          direction="column"
          maxH={["100%", "100vh"]}
        >
          {/* BACK BUTTON */}

          {isResetPasswordOpen && (
            <Box
              position={["relative", "absolute"]}
              top={["initial", "2rem"]}
              left={[`1.5rem`, `calc(29% + 2rem)`]}
              zIndex={10000}
            >
              <IconButton
                border={`1px solid ${newBorder}`}
                size="md"
                borderRadius="100rem"
                bg={white}
                fontSize="20px"
                icon={<Icon h="0.875rem" as={FaArrowLeft} />}
                onClick={() => {
                  setResetPasswordOpen(false);
                  dispatch({ type: AUTH_VERIFY_LOGIN_CODE_RESET });
                  dispatch({ type: AUTH_LOGIN_SUCCESS, payload: {} });
                  dispatch({ type: AUTH_GOOGLE_LOGIN_SUCCESS, payload: {} });
                }}
              />
            </Box>
          )}

          {!isResetPasswordOpen ? (
            <>
              {/* LOGIN FORM  START*/}
              <Flex
                w="100%"
                maxW={["100%", "420px"]}
                direction="column"
                position="relative"
                zIndex={10}
                bg={white}
                borderTopLeftRadius={["2rem", "0"]}
                borderTopRightRadius={["2rem", "0"]}
                p={["2rem 1.5rem 1.5rem", "initial"]}
              >
                {!showVerification && (
                  <Heading
                    fontSize={["1.875rem", "1.875rem"]}
                    fontWeight="700"
                    color={textPrimary}
                    mb={6}
                    zIndex={1}
                    textAlign={["left", "left"]}
                  >
                    {t("common.loginHeading")}
                  </Heading>
                )}

                {!loginLoading ? (
                  <>
                    <Flex direction="column">
                      {/* MAIN */}
                      {!showVerification && (
                        <>
                          <form
                            onSubmit={handleSubmit}
                            style={{ width: "100%" }}
                          >
                            <Box display={["flex", "block"]}>
                              <GoogleButton
                                label={t("common.loginGoogle")}
                                isWhite
                                onClick={loginWithGoogle}
                                loading={googleLoginLoading}
                              />
                            </Box>
                            <Box
                              position="relative"
                              py={3}
                              mb={6}
                              display={["flex", "block"]}
                            >
                              <Divider />
                              <AbsoluteCenter bg="none" px="8">
                                <Text
                                  fontSize="sm"
                                  whiteSpace="nowrap"
                                  bg={white}
                                  p="0 1rem"
                                  color={textSecondary}
                                  fontWeight="500"
                                >
                                  {t("common.loginOr")}
                                </Text>
                              </AbsoluteCenter>
                            </Box>
                            <FormControl>
                              <FormLabel htmlFor="email">
                                {t("form.email")}
                              </FormLabel>
                              <Input
                                id="email"
                                type="email"
                                value={email}
                                bg={[
                                  "rgba(255, 255, 255, 0.6)",
                                  "none",
                                  "none",
                                ]}
                                onChange={handleEmailChange}
                                required
                                size="lg"
                                h="3.5rem"
                                fontSize="1rem"
                                fontWeight="500"
                                border={[
                                  `1px solid ${newBorder}`,
                                  `1px solid ${newBorder}`,
                                ]}
                                textTransform="lowercase"
                                borderRadius={["10rem", "10rem", "1rem"]}
                                placeholder={t("placeholder.email")}
                                mb={4}
                                _focus={{
                                  border: `1px solid ${primary}`,
                                  boxShadow: "none !important",
                                }}
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel fontSize="1rem" htmlFor="password">
                                {t("form.password")}
                              </FormLabel>
                              <PasswordInput
                                value={password}
                                onChange={handlePasswordChange}
                              />
                            </FormControl>
                            <Flex
                              w="100%"
                              justify="flex-end"
                              align="center"
                              mt={2}
                            >
                              <Button
                                variant="text"
                                fontSize="0.875rem"
                                fontWeight="500"
                                color={textSecondary}
                                p={0}
                                size="sm"
                                _hover={{
                                  color: textPrimary,
                                }}
                                onClick={() => setResetPasswordOpen(true)}
                              >
                                {t("common.forgotPassword")}
                              </Button>
                            </Flex>
                            <Stack mt={4} mb="2rem">
                              <Button
                                onClick={handleSubmit}
                                type="submit"
                                h="3.5rem"
                                fontSize="md"
                                bg={dark}
                                color={white}
                                w="100%"
                                borderRadius="10rem"
                                _hover={{
                                  bg: primaryAlt,
                                  color: white,
                                }}
                              >
                                {t("common.signin")}
                              </Button>
                            </Stack>
                          </form>
                        </>
                      )}
                    </Flex>
                  </>
                ) : (
                  <Flex
                    direction="column"
                    justify="center"
                    align="center"
                    my={20}
                  >
                    <Spinner size="xl" />
                  </Flex>
                )}

                <>
                  {!showVerification && (
                    <Flex
                      // mt={4}
                      w="100%"
                      align="center"
                      justify="center"
                      position="relative"
                    >
                      <Text
                        fontSize={["1rem", "0.875rem"]}
                        fontWeight="500"
                        color={textPrimary}
                      >
                        {t("common.dontHaveAccount")}
                      </Text>
                      <Link to="/register">
                        <Button
                          ml={1}
                          variant="link"
                          color={primary}
                          p={0}
                          size="sm"
                          role="group"
                          _hover={{
                            color: primaryAlt,
                            transition: "0.2s all",
                          }}
                        >
                          <Text
                            as="span"
                            fontSize={["1rem", "0.875rem"]}
                            color={primary}
                            _groupHover={{
                              pr: "0.25rem",
                              color: primaryAlt,
                              transition: "0.2s all",
                            }}
                          >
                            {t("common.signUp")}
                          </Text>{" "}
                          <Icon ml={1} h="0.6rem" as={FaArrowRightLong} />
                        </Button>
                      </Link>
                    </Flex>
                  )}
                </>
              </Flex>
            </>
          ) : (
            <RequestResetPassword
              loginWithGoogle={loginWithGoogle}
              googleLoginLoading={googleLoginLoading}
              setResetPasswordOpen={setResetPasswordOpen}
            />
          )}
          {/* LOGIN FORM  END*/}
        </Flex>
      </Flex>
    </>
  );
};

const Video = styled.video`
  display: block;
  width: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  height: 100%;
  z-index: -1;
  position: absolute;
`;

export default Login;
